// Vehicles service - moved from database.js
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  serverTimestamp, 
  db 
} from '../../utils/database/firebase-config';
import { odometerService } from './odometer';
import cache from '../../utils/dataCache';

export const vehiclesService = {
  async getVehicles(companyId) {
    const key = `vehicles:${companyId}`;
    const cached = cache.get(key);
    if (cached) return cached;
    const vehiclesRef = collection(db, 'vehicles');
    const q = query(vehiclesRef, where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    const vehicles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Enrich with latest odometer readings in parallel
    await Promise.all(vehicles.map(async (vehicle) => {
      try {
        vehicle.latestOdometerReading = await odometerService.getLatestReading(companyId, vehicle.id);
      } catch (error) {
        console.error(`Error getting odometer for vehicle ${vehicle.id}:`, error);
        vehicle.latestOdometerReading = null;
      }
    }));

    cache.set(key, vehicles);
    return vehicles;
  },
  async getVehicle(vehicleId) {
    const vehicleRef = doc(db, 'vehicles', vehicleId);
    const snapshot = await getDoc(vehicleRef);
    if (snapshot.exists()) {
      const vehicle = { id: snapshot.id, ...snapshot.data() };
      
      // Enrich with odometer data
      try {
        const [latestReading, odometerStats] = await Promise.all([
          odometerService.getLatestReading(vehicle.companyId, vehicleId),
          odometerService.getVehicleOdometerStats(vehicle.companyId, vehicleId)
        ]);
        
        vehicle.latestOdometerReading = latestReading;
        vehicle.odometerStats = odometerStats;
      } catch (error) {
        console.error(`Error getting odometer data for vehicle ${vehicleId}:`, error);
        vehicle.latestOdometerReading = null;
        vehicle.odometerStats = null;
      }
      
      return vehicle;
    }
    return null;
  },
  async addVehicle(vehicleData) {
    const vehiclesRef = collection(db, 'vehicles');
    cache.invalidatePrefix('vehicles:');
    const docRef = await addDoc(vehiclesRef, { 
      ...vehicleData, 
      currentOdometer: vehicleData.initialOdometer || 0,
      odometerUpdatedAt: serverTimestamp(),
      createdAt: serverTimestamp(), 
      updatedAt: serverTimestamp() 
    });
    
    // Add initial odometer reading if provided
    if (vehicleData.initialOdometer && vehicleData.initialOdometer > 0) {
      try {
        await odometerService.addReading(vehicleData.companyId, docRef.id, {
          reading: vehicleData.initialOdometer,
          source: 'manual',
          notes: 'Initial odometer reading when vehicle was added',
          recordedBy: vehicleData.createdBy || 'system'
        });
      } catch (error) {
        console.error('Error adding initial odometer reading:', error);
      }
    }
    
    return docRef.id;
  },
  async updateVehicle(vehicleId, vehicleData) {
    const vehicleRef = doc(db, 'vehicles', vehicleId);
    await updateDoc(vehicleRef, { ...vehicleData, updatedAt: serverTimestamp() });
    cache.invalidatePrefix('vehicles:');
    return true;
  },
  async deleteVehicle(vehicleId) {
    const vehicleRef = doc(db, 'vehicles', vehicleId);
    await deleteDoc(vehicleRef);
    cache.invalidatePrefix('vehicles:');
    return true;
  },

  // Add odometer reading to vehicle
  async addOdometerReading(vehicleId, reading) {
    try {
      const vehicle = await this.getVehicle(vehicleId);
      if (!vehicle) throw new Error('Vehicle not found');
      
      return await odometerService.addReading(vehicle.companyId, vehicleId, reading);
    } catch (error) {
      console.error('Error adding odometer reading:', error);
      throw error;
    }
  },

  // Get vehicle odometer history
  async getVehicleOdometerHistory(vehicleId) {
    try {
      const vehicle = await this.getVehicle(vehicleId);
      if (!vehicle) throw new Error('Vehicle not found');
      
      return await odometerService.getVehicleReadings(vehicle.companyId, vehicleId);
    } catch (error) {
      console.error('Error getting odometer history:', error);
      throw error;
    }
  },

  // Import odometer readings from fuel data
  async importOdometerFromFuel(vehicleId) {
    try {
      const vehicle = await this.getVehicle(vehicleId);
      if (!vehicle) throw new Error('Vehicle not found');
      
      return await odometerService.importFromFuelTransactions(vehicle.companyId, vehicleId);
    } catch (error) {
      console.error('Error importing odometer from fuel:', error);
      throw error;
    }
  }
};